import type { EventType } from '../api/eventTypes';
import type { EventFormValues } from './EventForm';

/** Which boxes a type filled in, so the form can say so rather than leaving it to be discovered. */
export type PrefilledFields = Partial<Record<keyof EventFormValues, true>>;

export interface Prefill {
  values: Partial<EventFormValues>;
  prefilled: PrefilledFields;
}

/**
 * What picking a kind puts in the form.
 *
 * **Only into empty boxes, and only ones the leader has not typed in.** A field the leader filled
 * is theirs; a field a previous type filled is fair game, because switching from Campout to Troop
 * Meeting and keeping the campground address would be worse than either answer alone.
 *
 * Nothing here is stored on the type or read back through it. The form holds ordinary strings from
 * this point on, and what is saved is what the boxes hold — see §9 on why a live link would let an
 * officer tidying a type silently move a trip forty families have already read.
 */
export function prefillFrom(
  type: EventType | undefined,
  values: EventFormValues,
  alreadyPrefilled: PrefilledFields,
): Prefill {
  const next: Partial<EventFormValues> = {};
  const prefilled: PrefilledFields = {};

  if (!type) {
    return { values: next, prefilled };
  }

  const { defaults } = type;

  /** Free for this type to fill: empty, or filled by the last type rather than by a person. */
  const isAvailable = (field: keyof EventFormValues) =>
    !values[field] || alreadyPrefilled[field] === true;

  const put = (field: keyof EventFormValues, value: string | null | undefined) => {
    if (!value || !isAvailable(field)) return;
    next[field] = value;
    prefilled[field] = true;
  };

  put('description', defaults.description);
  put('locationName', defaults.locationName);
  put('locationAddress', defaults.locationAddress);

  // --- the times ----------------------------------------------------------
  //
  // A default is a reading off a clock with no date in it, so it needs one. The date already in
  // the box is used where there is one, and today's otherwise -- a visible date the leader will
  // change is more use than an empty box, and it is the only way "picking a kind fills the times"
  // can be true before a date has been chosen.
  //
  // The string produced is a local reading, `2026-10-30T17:00`, exactly as the API expects. It is
  // never turned into an instant here: the unit's zone is what it means, and the browser is
  // regularly in a different one.
  const startDate = dateOf(values.startsAt) ?? today();

  if (defaults.startTimeOfDay && isAvailable('startsAt')) {
    next.startsAt = `${startDate}T${defaults.startTimeOfDay}`;
    prefilled.startsAt = true;
  }

  const effectiveStart = next.startsAt ?? values.startsAt;

  if (isAvailable('endsAt') && effectiveStart) {
    const end = endFrom(effectiveStart, defaults.endTimeOfDay, defaults.durationMinutes);

    if (end) {
      next.endsAt = end;
      prefilled.endsAt = true;
    }
  }

  return { values: next, prefilled };
}

/**
 * The end of an event that starts at `start`.
 *
 * A length and an end time are alternatives the API refuses to hold together, so at most one of
 * these applies. An end time earlier in the day than the start means the next morning — a lock-in
 * that ends at 07:00 is the ordinary case, not a mistake.
 */
function endFrom(
  start: string,
  endTimeOfDay: string | null,
  durationMinutes: number | null,
): string | undefined {
  if (endTimeOfDay) {
    const day = dateOf(start);
    if (!day) return undefined;

    return timeOf(start) && endTimeOfDay <= timeOf(start)!
      ? `${addDays(day, 1)}T${endTimeOfDay}`
      : `${day}T${endTimeOfDay}`;
  }

  if (durationMinutes) {
    // Arithmetic on the reading, not on an instant. `new Date('2026-03-08T02:00')` would be
    // resolved in the browser's zone, and adding minutes across a daylight-saving boundary there
    // gives an hour that the unit's zone never had.
    return addMinutes(start, durationMinutes);
  }

  return undefined;
}

const dateOf = (reading: string): string | undefined => reading.split('T')[0] || undefined;
const timeOf = (reading: string): string | undefined => reading.split('T')[1] || undefined;

function today(): string {
  const now = new Date();
  return [
    now.getFullYear(),
    `${now.getMonth() + 1}`.padStart(2, '0'),
    `${now.getDate()}`.padStart(2, '0'),
  ].join('-');
}

/** Day arithmetic on a plain date, via UTC so no zone can shift it. */
function addDays(date: string, days: number): string {
  const [y, m, d] = date.split('-').map(Number);
  const shifted = new Date(Date.UTC(y, m - 1, d + days));
  return shifted.toISOString().slice(0, 10);
}

function addMinutes(reading: string, minutes: number): string | undefined {
  const date = dateOf(reading);
  const time = timeOf(reading);
  if (!date || !time) return undefined;

  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);

  // UTC arithmetic on a zoneless reading: the point is to add minutes to a clock face, and a
  // zone-aware Date would add or drop an hour at a daylight-saving boundary.
  const shifted = new Date(Date.UTC(y, m - 1, d, hh, mm + minutes));

  return `${shifted.toISOString().slice(0, 10)}T${shifted.toISOString().slice(11, 16)}`;
}
